import type {
  B2bCampaignActionHandler,
  B2bContract,
  B2bContractStatus,
  B2bContractType,
  B2bMatchingContributionPaymentFrequency,
  B2bPaperTransactionStatementFrequency,
} from '@Common/types/bo/b2b/contracts';
import { B2bContractFees } from '@Common/types/bo/b2b/contracts/fees';

const feeFields = {
  company: {
    file_opening: 'Frais d’ouverture de dossier',
    calculating_individual_allocations: 'Calcul des quotes-parts individuelles (par opération)',
    integration_of_beneficiary_data: 'Intégration des données des bénéficiaires (par fichier)',
    integration_of_affiliates_choices_via_mail:
      'Intégration des retours des bénéficiaires sur leur choix de placement (par opérations)',
    integration_of_affiliates_consultation_data:
      'Intégration des retours des salariés hors Espace Client après interrogation réalisée par vos soins (par fichier)',
    rejection_of_direct_debit_or_bank_transfer:
      'Rejet de prélèvement ou de virement (par incident)',
    extra_reminder_for_unpaid_invoices: 'Relance pour facture impayée, après 2 relances',
    collective_transfer_to_another_manager:
      'Transfert collectif vers un autre gestionnaire (prix par compte, avec un max. de €1000 par entreprise)',
  } satisfies Record<keyof B2bContractFees['company'], string>,
  affiliate: {
    statements_and_certificates:
      "relevés et attestations papiers (IFU, relevé d'opération, relevé annuel)",
    annual_custody_for_former_or_retired_employees:
      'Forfait annuel de tenue de compte des anciens salariés ou retraités',
    annual_custody_pereco_pct: 'Tenue de compte PERECO des anciens salariés ou retraités',
    annual_custody_pereco_max:
      'Plafond de tenue de compte PERECO des anciens salariés ou retraités',
    account_position_at_given_date:
      'Édition de position de compte à une date donnée (hors succession)',
    undelivered_mail_handling: 'Traitement d’un courrier non distribué',
    pledging_or_release_of_assets: 'Nantissement ou de mainlevée de vos avoirs',
    search_for_documents_or_transactions_before_10_years:
      'Recherche de documents ou d’opérations spécifiques (chèque émis, virement rejeté…) datant de moins de 10 ans',
    search_for_documents_or_transactions_after_10_years:
      'Recherche de documents ou d’opérations spécifiques (chèque émis, virement rejeté…) datant au-delà de 10 ans',
    transfer_from_pee_to_another_manager:
      'Transfert individuel sortant du PEE vers un autre gestionnaire',
    transfer_from_pereco_to_another_manager_pct:
      'Transfert individuel sortant du PERECO vers un autre gestionnaire',
    estate_settlement_low_amount: 'Règlement d’une succession entre 500 et 5 000 €',
    estate_settlement_high_amount: 'Règlement d’une succession > 5 000 €',
    account_closure: 'Clôture de compte (frais de dossier + archivage 30 ans)',
    specific_mail_requests_processing: 'Tous traitements de demandes spécifiques',
    caisse_des_depots_recording: 'Consignation à la Caisse des Dépôts',
    voluntary_payment_by_cheque: 'Versement volontaire par traitement de chèque bancaire',
    repayment_of_savings: 'Remboursement de votre épargne (disponible et/ou indisponible)',
    retractation_following_pereco_payment: 'Rétractation suite à un versement sur le PERECO',
    direct_debit_rejection: 'Rejet de prélèvement',
    unpaid_cheques: 'Frais de chèque impayé',
    repayment_reimbursement_triggered: 'Remboursement avec un seuil de déclenchement',
    repayment_by_cheque: 'Règlement par chèque bancaire',
    repayment_by_bank_transfer: 'Règlement par virement',
    repayment_settlement_reissuing: 'Réémission d’un règlement',
  } satisfies Record<keyof B2bContractFees['affiliate'], string>,
  static: {
    consultation_of_affiliates_per_mail: {
      label: 'Interrogation des bénéficiaires sur leur choix de placement',
      value: 'Coût réel',
    },
    time_savings_account_gateway: {
      label: 'Passerelle Compte Epargne Temps',
      value: 'Sur devis',
    },
  },
};

export const b2bContractsTranslations = {
  fields: {
    id: 'ID Contrat',
    b2b_company_id: 'ID Entreprise',
    team: 'Équipe',
    contract_number: 'Numéro de contrat',
    wrong_contract_number:
      'Seules les lettres, les chiffres, les traits de soulignement et les tirets sont autorisés',
    contract_type: 'Type de contrat',
    label: 'Label',
    support_phone_number: 'Numéro de téléphone du support',
    insurer_company: "Compagnie d'assurance",
    status: 'Statut du contrat',
    signature_date: 'Date de signature du contrat',
    effective_date: "Date d'effet",
    end_date: 'Date de fin du contrat',
    is_active: 'Est actif',
    relationship_type: 'Type de relation',
    paper_transaction_statement_frequency:
      'Fréquence de la version papier des relevés de transaction',
    version: 'Version',
    version_label: 'Étiquette de version',
    is_group_contract: 'Le contrat est-il un contrat de groupe',
    is_agreement_common_for_group: "L'accord est-il commun au groupe",
    is_matching_contribution_displayed: "Abondement affiché sur l'espace personnel salarié",
    participation: {
      agreement_starting_date: 'Date de démarrage de la participation',
      agreement_end_date: 'Date de fin de la participation',
      is_agreement_common_for_group: "L'accord de participation est-il commun au groupe",
      distribution_uniformity_pct: 'Uniformément',
      distribution_salary_pct: 'Proportionellement au salaire reçu',
      distribution_tenure_pct: 'Proportionnellement au temps de présence',
    },
    profit_sharing: {
      agreement_starting_date: "Date de démarrage de l'intéressement",
      agreement_end_date: 'Date de fin de l’intéressement',
      is_tacitly_renewed: "L'accord de participation aux bénéfices est-il reconduit tacitement ?",
      is_agreement_common_for_group: "L'accord d’intéressement est-il commun au groupe ?",
      distribution_uniformity_pct: 'Uniformément',
      distribution_salary_pct: 'Proportionellement au salaire reçu',
      distribution_tenure_pct: 'Proportionnellement au temps de présence',
    },
    campaigns: {
      allocation_computed_by: 'Calcul des quotes-parts par',
      choice_collected_by: 'Collecte des choix par',
      payout_by: 'Paiements directs aux salariés par',
      matching_contribution_computed_by: 'Abondement calculé par',
    },
    value_sharing: {
      agreement_starting_date: 'Date de démarrage de l’accord de partage de la valeur',
      agreement_end_date: 'Date de fin de l’accord de partage de la valeur',
      is_agreement_common_for_group: "L'accord de partage de la valeur est-il commun au groupe ?",
    },
    unused_vacation_days: {
      agreement_starting_date: 'Date de démarrage du CET',
      agreement_end_date: 'Date de fin du CET',
      is_agreement_common_for_group: "L'accord du CET est-il commun au groupe ?",
      increment_step: 'Incrément par pas de',
    },
    monetized_days_off: {
      increment_step: 'Incrément par pas de',
    },
    pee_agreement: {
      agreement_starting_date: 'Date de démarrage de l’accord PEE',
      agreement_end_date: 'Date de fin de l’accord PEE',
      is_agreement_common_for_group: "L'accord PEE est-il commun au groupe ?",
    },
    pereco_agreement: {
      agreement_starting_date: 'Date de démarrage de l’accord PERECO',
      agreement_end_date: 'Date de fin de l’accord PERECO',
      is_agreement_common_for_group: "L'accord PERECO est-il commun au groupe ?",
    },
    pero_agreement: {
      agreement_starting_date: 'Date de démarrage de l’accord PERO',
      agreement_end_date: 'Date de fin de l’accord PERO',
      is_agreement_common_for_group: "L'accord PERO est-il commun au groupe ?",
    },
    transfer: {
      is_competition_transfer: 'Transfert en provenance de la concurrence',
      external_custodian: 'Transfert depuis le dépositaire',
      external_custodian_select: 'Dépositaire',
      external_custodian_other: 'Autre dépositaire',
      is_soles_transfer: 'Transfert ancien Standard AXA en SOLES',
    },
    created_at: 'Créé le',
    updated_at: 'Mis à jour le',
    dist_case_number: 'Numéro de dossier',
    dist_case_reception_date: 'Date de réception',
    dist_correspondent_id: 'Distributeur',
    dist_correspondent_company_name: 'Company name',
    dist_correspondent_assistance_email: 'Assistance email',
    dist_correspondent_firstname: 'Prénom',
    dist_correspondent_lastname: 'Nom',
    dist_correspondent_portfolio_code: 'Code portefeuille',
    dist_correspondent_orias_code: 'Numéro ORIAS',
    dist_correspondent_fia_code: 'Numéro FIA',
    dist_correspondent_email: 'Email',
    dist_correspondent_landline: 'Téléphone fixe',
    dist_correspondent_phone_number: 'Téléphone mobile',
    dist_correspondent_collective_inspector_email: 'Mail de l’inspecteur collectives',
    dist_correspondent_street: 'Rue',
    dist_correspondent_postal_code: 'Code postal',
    dist_correspondent_city: 'Ville',
    dist_correspondent_country: 'Pays',
    dist_status: 'Statut',
    matching_contribution_cut_off_date: 'Date du dernier versement annuel (JJ/MM)',
    matching_contributions_enabled: 'Abondement activé',
    matching_contribution_payment_frequency: "Fréquence de paiement de l'abondement",
    total_contribution_ceiling: 'Plafond total des abondements pour tous les plans',
    is_liv: 'Loi Industrie Verte',
    filib_url: 'FILIB URL',
    offer_code: 'Code groupe',
    fees: feeFields,
    ghost_login: 'Login fantôme',
  },
  initial_questions: {
    participation: "L'entreprise a-t-elle un accord de participation ?",
    value_sharing: "L'entreprise a-t-elle un accord de partage de la valeur ?",
    profit_sharing: "L'entreprise a-t-elle un accord d'intéressement ?",
    unused_vacation_days: "L'entreprise a-t-elle un accord de CET ?",
    pee_agreement: "L'entreprise a-t-elle un PEE ?",
    pereco_agreement: "L'entreprise a-t-elle un PERECO ?",
    pero_agreement: "L'entreprise a-t-elle un PERO ?",
    transfer: "L'entreprise a-t-elle un accord de transfert ?",
    participation_is_agreement_common_for_group:
      "L'accord de participation est-il commun à l'ensemble du groupe ?",
    value_sharing_is_agreement_common_for_group:
      "L'accord de partage de la valeur est-il commun à l'ensemble du groupe ?",
    profit_sharing_is_agreement_common_for_group:
      "L'accord d'intéressement est-il commun à l'ensemble du groupe ?",
    unused_vacation_days_is_agreement_common_for_group:
      "L'accord de CET est-il commun à l'ensemble du groupe ?",
    pee_is_agreement_common_for_group:
      "Le type d'accord PEE est-il commun à l'ensemble du groupe ?",
    pereco_is_agreement_common_for_group:
      "Le type d'accord PERECO est-il commun à l'ensemble du groupe ?",
    pero_is_agreement_common_for_group:
      "Le type d'accord PERO est-il commun à l'ensemble du groupe ?",
  },
  choices: {
    relationship_type: {
      direct: 'Directe',
      indirect: 'Indirecte',
    } satisfies Record<B2bContract['relationship_type'], string>,
    campaign_action_handler: {
      distributor: 'Teneur de compte',
      company: 'Entreprise',
    } satisfies Record<B2bCampaignActionHandler, string>,
    days_increment_step: {
      quarter_day: '0,25',
      half_day: '0,5',
    },
    team: {
      standard: 'Standard',
      middle: 'Middle (PME)',
      key_account: 'Grand compte',
      international: 'Internationale',
    },
    paper_transaction_statement_frequency: {
      digital_only: 'Digital uniquement',
      '1_month': 'Mensuelle',
      '3_months': '3 mois',
      '6_months': '6 mois',
      '12_months': '12 mois',
      every_time: 'À chaque opération',
    } satisfies Record<B2bPaperTransactionStatementFrequency, string>,
    matching_contribution_payment_frequency: {
      '1_month': 'Mensuelle',
      '3_months': '3 mois',
      '6_months': '6 mois',
      '12_months': '12 mois',
    } satisfies Record<B2bMatchingContributionPaymentFrequency, string>,
    contract_type: {
      soles: 'SOLES',
      es_custom: 'ES sur-mesure',
      er_standard: 'ER standard',
      er_custom: 'ER sur-mesure',
      er_external: 'ER Externe',
      essentiel: 'Offre Essentiel',
      flexible: 'Offre Flexible',
      patrimonial: 'Offre Patrimonial',
      premium: 'Offre Premium',
      sur_mesure: 'Sur mesure Malakoff Humanis',
    } satisfies Record<B2bContractType, string>,
    status: {
      offer: 'Offre',
      effective: 'En vigueur',
      suspended: 'Suspendu',
      denounced: 'Dénoncé',
      process_of_creation: 'En cours de création',
      closed_by_amendment: 'Clôturé par avenant',
      other: 'Autre',
      terminated: 'Résilié',
    } satisfies Record<B2bContractStatus, string>,
    custody_fees_amounts_per: {
      employees: 'Employés',
      products: 'Produits',
      flat_rate: 'Forfait',
    },
  },
  menus: {
    details: 'Détails',
    agreements: 'Accords',
    campaigns: 'Traitement des opérations',
    plans: 'Produits',
    documents: 'Documents',
    matching_contributions: 'Abondements',
    fees: 'Frais',
    distributor: {
      label: 'Information distributeur',
      headers: {
        distributor_correspondent: 'Détails correspondant distributeur',
        address: 'Adresse',
      },
    },
  },
  sections: {
    participation: 'Participation',
    participation_distribution: 'Détails de la distribution de la participation',
    profit_sharing: 'Intéressement',
    profit_sharing_distribution: 'Détails de la distribution du partage des bénéfices',
    value_sharing: 'Partage de la valeur',
    voluntary_deposit: 'Versement volontaire entreprise',
    unused_vacation_days: 'Compte épargne-temps',
    monetized_days_off: 'Jours de repos monétisés',
    pee_agreement: 'PEE (PEE / PEI / PEEG)',
    pereco_agreement: 'PERECO (PERECO / PERECO-I / PERECOG)',
    pero_agreement: 'PERO (PERO / PERU) ',
    transfer: 'Transfert',
    amendment: 'Avenant',
    no_agreement: "Pas d'accord défini",
  },
  campaigns: {
    has_processing_options_by_cash_sources:
      'Souhaitez-vous configurer les paramètres de traitement des opérations pour chaque source de trésorerie ?',
    processing_options_by_cash_sources_next_step:
      "Le paramétrage pour chaque source de trésorerie se fera à l'étape suivante.",
    matching_contribution_computed_by_edit_warning:
      "Vous avez modifié le responsable du calcul de l'abondement. Pensez à mettre également à jour la configuration des plans en conséquence.",
    processing_options_edit_warning:
      "Cette modification n'aura pas d'effet rétroactif sur les campagnes en cours utilisant ce contrat.",
    all: 'Traitement de toutes les opérations (P+I+PPV)',
    participation: 'Participation',
    profit_sharing: 'Intéressement',
    value_sharing_bonus: 'Prime de partage de la valeur',
    voluntary_deposit: 'Versement volontaire entreprise',
    unused_vacation_days: 'Compte épargne-temps',
  },
  amendment: {
    title: 'Avenant au contrat',
    label: 'Étiquette',
    version: 'Numéro de version',
    signed_amendment_document: "Télécharger l'avenant au contrat signé",
    notes: 'Notes',
    applies_at: "Date d'entrée en vigueur du changement",
  },
  matching_contributions: {
    disable_edit_warning:
      "La désactivation de l'abondement n'annule pas les abondements en attente : ils seront prélevés le mois prochain.",
  },
  distributor_info: {
    case: 'Détails du dossier',
    correspondent: 'Détails du correspondant distributeur',
    distributor: 'Distributeur',
    address: 'Adresse',
    mailing_address: 'Adresse Postale',
    search_company: "Entrez le nom de l'entreprise",
    search_company_label: 'Nom de la société de distribution',
    create_new_hint: 'Vous ne trouvez pas de distributeur ? Créez-en un dans la',
    create_new_link: 'section Distributeurs',
    view_distributor_link: 'Voir le distributeur',
  },
  distribution_details: {
    rule: 'Règle',
    pct: '%',
    total: 'Total',
  },
  fees: {
    sections: {
      details: 'Détails de la grille tarifaire',
      custody: 'Frais de tenue de compte',
      company: 'Frais annexes payées par l’Entreprise',
      collective_operation: 'Frais liés aux opérations collectives',
      amount_excl_vat: 'Montant hors TVA',
      amount_incl_vat: 'Montant TTC',
      others: 'Autres frais annexes',
      affiliate: 'Frais annexes à charge du Salarié',
      savings_management: "Gestion de l'épargne",
      current_operations: 'Opérations courantes',
      repayment: 'Remboursements',
      amounts_per: 'Montants par:',
      fee_code: 'Code frais',
      use_old_pricing: 'Utiliser l’ancienne grille tarifaire ?',
      warning:
        'Si l’épargne est uniquement sur PERECO, les frais de tenue de compte à charge de l’affilié seront fixés à 5% (plafonnés à €20).',
    },
    subtitles: {
      eur_vat_excluded: 'Les montants sont en € et hors TVA',
      eur_vat_included: 'Les montants sont en € et TTC',
      free_if_done_online: 'Gratuit via solution digitale',
      amounts_per_employees: 'Les montants sont par employés',
      amounts_per_products: 'Les montants sont par produits',
      amounts_per_flat_rate: 'Les montants sont au forfait',
      old_pricing: 'Ancienne grille tarifaire',
    },
    custody: {
      amount: {
        header: 'Encours moyen',
        col1: 'Effectif',
        col2: '<= 15 000€',
        col3: '> 15 000€',
        row1: '[1 - 11]',
        row2: '[12 - 249+]',
      },
      paid_by: {
        employee_status: "Statut de l'employé",
        paid_by: 'Payé par',
        current: 'Présent',
        past: 'Sorti',
        retired: 'Retraité',
        amount: 'Montant',
        amount_pee: 'Montant par PEE',
        amount_pereco: 'Montant par PERECO',
        amount_incl_vat: '(TTC)',
        amount_excl_vat: '(HTVA)',
      },
    },
    flat_rate: {
      fee_label: 'FE_forfait_pee_pereco',
    },
    old_pricing: {
      flat_rate: 'FE_bse_forfait_tc',
      fees_amount: 'FE_bse_frais_tc',
    },
  },
  actions: {
    create: {
      title: 'Créer Contract',
      success: 'Contract créé',
      failure: 'Échec lors de la création du contrat: %{error}',
      steps: {
        '1': 'Information générale',
        '2': 'Distributor info',
        '3': 'Accords',
        '4': 'Traitement des opérations',
        '5': 'Frais',
      },
    },
    edit: {
      simple: {
        button: 'Éditer le contrat',
        title: 'Éditer le contrat',
      },
      button: 'Créer un avenant',
      title: 'Éditer le contrat',
      success: 'Contrat mise à jour',
      failure: 'Échec lors de la mise à jour du contrat: %{error}',
      steps: {
        '1': 'Frais',
        '2': 'Avenant',
      },
    },
  },
  validation: {
    distribution_total_pct: 'Le total doit être égal à 100%',
    participation: 'Si au moins un champ Participation est défini, tous doivent être définis',
    profit_sharing: 'Si au moins un champ Intéressement est défini, tous doivent être définis',
    transfer: 'Au moins un des champs doit être sélectionné',
    dd_mm_date: 'Doit être une date au format JJ/MM',
    agreement_starting_date: 'La date de début doit être inférieur à la date de fin',
    agreement_end_date: 'La date de fin doit être supérieur à la date de début',
  },
  documents: {
    title: 'Documents',
    empty: 'Ce contrat ne contient pas encore de documents',
    labels: {
      created_at: 'Créé le',
      type: 'Type',
      name: 'Nom',
      id: 'ID Document',
      url: 'Url',
    },
    button: 'Télécharger Document',
    create: {
      title: 'Télécharger le document du contrat',
      success: 'Document téléchargé',
      failure: 'Erreur lors du téléchargement du document: %{error}',
      label: 'Télécharger Document',
    },
    type: {
      employee_savings_booklet: 'Livret d’épargne salariale',
      general_terms_and_conditions: 'Conditions générales',
      plan_regulations: 'Règlement de plan',
      amendments: 'Avenants',
      price_list_employee: 'Grille tarifaire employé',
      price_list_company: 'Grille tarifaire entreprise',
      signed_contract_amendment: 'Avenant au contrat signé',
    },
  },
};
